import { motion } from "framer-motion";

const badges = ["AWS", "Docker", "Kubernetes", "CI/CD", "Jenkins", "Terraform", "GitHub Actions", "Linux", "Angular", "Next.js"];

export default function Hero() {
  return (
    <section
      id="hero"
      className="min-h-screen grid-bg flex items-center justify-center relative overflow-hidden pt-20"
    >
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-10">

          {/* Left - Text */}
          <div className="w-full md:flex-1 text-center md:text-left min-w-0">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-wrap justify-center md:justify-start gap-2 mb-6"
            >
              {badges.map((b, i) => (
                <motion.span
                  key={b}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.07 + 0.2 }}
                  className="bg-sky-500/10 border border-sky-500/30 text-sky-400 text-xs font-semibold px-3 py-1 rounded-full"
                >
                  {b}
                </motion.span>
              ))}
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-sky-400 font-semibold text-sm mb-2 tracking-widest uppercase"
            >
              Hello, I'm
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-4xl md:text-6xl font-black mb-3 leading-tight"
            >
              <span className="gradient-text">Veerabhadraswamy</span>
              <br />
              <span className="text-white">C T</span>
            </motion.h1>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-base md:text-xl font-semibold text-slate-300 mb-5"
            >
              Cloud &amp; DevOps Engineer{" "}
              <span className="text-sky-400">(Programmer Analyst)</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-slate-400 text-sm md:text-base max-w-xl mb-8 leading-relaxed mx-auto md:mx-0"
            >
              DevOps &amp; Full-Stack Engineer with hands-on experience deploying, automating, and managing
              production applications on <span className="text-sky-400 font-medium">AWS</span>, Docker, Kubernetes,
              and CI/CD pipelines. Based in{" "}
              <span className="text-white font-medium">Bengaluru, India</span>.
            </motion.p>

            {/* CTA Buttons - equal width, no emoji */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row justify-center md:justify-start gap-3 w-full"
            >
              <button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="w-full sm:w-44 bg-sky-500 hover:bg-sky-400 text-white font-bold px-6 py-3 rounded-full transition-all glow text-sm text-center"
              >
                Hire Me
              </button>
              <a
                href="/resume.pdf"
                download
                className="w-full sm:w-44 border border-sky-500/50 hover:border-sky-400 text-sky-400 hover:bg-sky-500/10 font-bold px-6 py-3 rounded-full transition-all text-sm text-center"
              >
                Download Resume
              </a>
            </motion.div>

            {/* Quick contact row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="flex flex-col sm:flex-row flex-wrap justify-center md:justify-start gap-3 mt-6 text-sm text-slate-400"
            >
              <a href="tel:+917022604157" className="hover:text-sky-400 transition-colors truncate">
                +91-7022604157
              </a>
              <span className="hidden sm:block text-slate-600">|</span>
              <a href="mailto:veerabhadraswamyct66@gmail.com" className="hover:text-sky-400 transition-colors truncate">
                veerabhadraswamyct66@gmail.com
              </a>
              <span className="hidden sm:block text-slate-600">|</span>
              <span>Bengaluru, India</span>
            </motion.div>
          </div>

          {/* Right - Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="flex-shrink-0"
          >
            <div className="relative mx-auto">
              <div className="w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden border-4 border-sky-500/50 glow">
                <img
                  src="/profile.jpg"
                  alt="Veerabhadraswamy C T"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3 }}
                className="absolute -bottom-4 -left-4 bg-[#0d1526] border border-sky-500/30 rounded-xl px-3 py-1.5 text-xs font-semibold text-sky-400 shadow-lg whitespace-nowrap"
              >
                DevOps
              </motion.div>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 3.5 }}
                className="absolute -top-4 -right-4 bg-[#0d1526] border border-green-500/30 rounded-xl px-3 py-1.5 text-xs font-semibold text-green-400 shadow-lg whitespace-nowrap"
              >
                Available
              </motion.div>
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="absolute -bottom-4 -right-4 bg-[#0d1526] border border-orange-500/30 rounded-xl px-3 py-1.5 text-xs font-semibold text-orange-400 shadow-lg whitespace-nowrap"
              >
                AWS
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-16 flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-sky-500/40 rounded-full flex justify-center pt-2"
          >
            <div className="w-1 h-2 bg-sky-400 rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
